import {
  Box, Button, Flex, Input, Text,
  Table, TableContainer, Tbody, Td, Th, Thead, Tr,
} from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { FiSearch } from "react-icons/fi";
import { api } from "../../../services/api";

const GREEN  = "#004a33";
const PAGE_SIZE = 25;

const inputProps = {
  borderRadius: "8px",
  fontSize: "13px",
  bg: "white",
  border: "1px solid rgba(0,74,51,.2)",
  _focus: { borderColor: GREEN, boxShadow: `0 0 0 1px ${GREEN}` },
};

function money(amount) {
  return Number(amount || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function fmtDate(val) {
  const d = new Date(val);
  if (isNaN(d)) return "—";
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" }) +
    " " + d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

export function CreditsOverviewCard() {
  const [credits, setCredits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    async function fetchCredits() {
      try {
        const response = await api.get("/api/v1/credits");
        setCredits(response.data || []);
      } catch (error) {
        console.error("Error fetching credits:", error);
      } finally {
        setLoading(false);
      }
    }
    fetchCredits();
  }, []);

  const filteredCredits = useMemo(() => {
    if (!search.trim()) return credits;
    const q = search.toLowerCase().trim();
    return credits.filter(c =>
      c.user?.full_name?.toLowerCase().includes(q) ||
      c.user?.email?.toLowerCase().includes(q)
    );
  }, [credits, search]);

  useEffect(() => { setPage(1); }, [search]);

  const totalPages = Math.max(1, Math.ceil(filteredCredits.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedCredits = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredCredits.slice(start, start + PAGE_SIZE);
  }, [filteredCredits, currentPage]);

  const totalAmount = filteredCredits.reduce((sum, c) => sum + Number(c.amount || 0), 0);

  return (
    <Box>
      {/* Section header */}
      <Flex align="center" justify="space-between" mb="20px" wrap="wrap" gap="10px">
        <Box>
          <Text fontSize="11px" fontWeight="800" color="gray.400" textTransform="uppercase" letterSpacing="0.08em">
            Plataforma
          </Text>
          <Text fontSize="20px" fontWeight="900" color={GREEN}>
            Créditos concedidos
            <Text as="span" fontSize="14px" fontWeight="600" color="gray.400" ml="8px">({credits.length})</Text>
          </Text>
        </Box>
        <Box textAlign="right">
          <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="0.06em">
            Total {search ? "filtrado" : "concedido"}
          </Text>
          <Text fontSize="18px" fontWeight="900" color={GREEN}>{money(totalAmount)}</Text>
        </Box>
      </Flex>

      {/* Search */}
      <Box position="relative" maxW="360px" mb="14px">
        <Box position="absolute" left="10px" top="50%" transform="translateY(-50%)" color="gray.400" pointerEvents="none">
          <FiSearch size={14} />
        </Box>
        <Input
          pl="32px"
          placeholder="Buscar por nome ou e-mail do usuário…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          {...inputProps}
          size="sm"
        />
      </Box>
      <Text fontSize="12px" color="gray.400" mb="14px">
        {filteredCredits.length} de {credits.length} crédito{credits.length !== 1 ? "s" : ""}
      </Text>

      {/* Table */}
      <Box bg="white" borderRadius="16px" border="1px solid rgba(0,74,51,.08)" boxShadow="0 2px 12px rgba(0,74,51,.05)" overflow="hidden">
        {loading ? (
          <Box py="48px" textAlign="center">
            <Text fontSize="14px" color="gray.400">Carregando…</Text>
          </Box>
        ) : filteredCredits.length === 0 ? (
          <Box py="48px" textAlign="center">
            <Text fontSize="15px" fontWeight="700" color="gray.400">
              {search ? `Nenhum crédito encontrado para "${search}"` : "Nenhum crédito concedido ainda"}
            </Text>
          </Box>
        ) : (
          <TableContainer>
            <Table size="sm" variant="simple">
              <Thead bg="rgba(0,74,51,.04)">
                <Tr>
                  <Th color={GREEN} fontSize="11px" py="12px">#</Th>
                  <Th color={GREEN} fontSize="11px">Usuário</Th>
                  <Th color={GREEN} fontSize="11px">Valor</Th>
                  <Th color={GREEN} fontSize="11px">Pagamento vinculado</Th>
                  <Th color={GREEN} fontSize="11px">Data</Th>
                </Tr>
              </Thead>
              <Tbody>
                {pagedCredits.map(c => (
                  <Tr key={c.id} _hover={{ bg: "rgba(0,74,51,.025)" }} transition="background 0.1s">
                    <Td py="10px">
                      <Text fontSize="12px" fontWeight="700" color={GREEN}>#{c.id}</Text>
                    </Td>
                    <Td>
                      <Text fontSize="12px" fontWeight="600" noOfLines={1} maxW="200px">{c.user?.full_name || "—"}</Text>
                      <Text fontSize="11px" color="gray.400" noOfLines={1} maxW="200px">{c.user?.email}</Text>
                    </Td>
                    <Td>
                      <Text fontSize="13px" fontWeight="800" color="#2f9e44">+{money(c.amount)}</Text>
                    </Td>
                    <Td>
                      <Text fontSize="12px" color="gray.600">pag.#{c.payment_id}</Text>
                    </Td>
                    <Td>
                      <Text fontSize="12px" color="gray.600">{fmtDate(c.created_at)}</Text>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        )}
        <Flex
          align="center" justify="space-between" px="20px" py="10px"
          borderTop="1px solid rgba(0,74,51,.06)" wrap="wrap" gap="8px"
        >
          <Text fontSize="12px" color="gray.400">
            {credits.length} crédito{credits.length !== 1 ? "s" : ""} no total
          </Text>
          {filteredCredits.length > 0 && (
            <Flex align="center" gap="10px">
              <Button
                size="xs" variant="ghost" color={GREEN} fontWeight="700" fontSize="11px"
                isDisabled={currentPage <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}
              >
                Anterior
              </Button>
              <Text fontSize="12px" color="gray.500" fontWeight="600">
                Página {currentPage} de {totalPages}
              </Text>
              <Button
                size="xs" variant="ghost" color={GREEN} fontWeight="700" fontSize="11px"
                isDisabled={currentPage >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              >
                Próxima
              </Button>
            </Flex>
          )}
        </Flex>
      </Box>
    </Box>
  );
}
